import React, { useEffect, useState } from 'react'
import { Navigate, Outlet } from 'react-router-dom'

import Loading from '@hapana/shared/web/components/shared/loading/loading.component'
import { usePermissions } from '_stores/permissions.store'
import useSiteStore from '_stores/site.store'
import { ROUTES } from '../../app.constants'

interface SettingsGuardState {
  isLoading: boolean
  shouldRedirect: boolean
  redirectPath: string | null
}

const SettingsGuard: React.FC = () => {
  const [state, setState] = useState<SettingsGuardState>({
    isLoading: true,
    shouldRedirect: false,
    redirectPath: null,
  })

  const {
    getAccessibleSites,
    isLoading: isPermissionsLoading,
    hasUniversalSiteAccess,
    meData,
  } = usePermissions()

  const { currentSite, hasExplicitBrandIntent } = useSiteStore()

  const hasUserData = !!meData?.me
  const accessibleSiteIds = hasUserData ? getAccessibleSites() : []
  const hasUniversalAccess = hasUserData ? hasUniversalSiteAccess() : false

  const accessibleSiteCount = accessibleSiteIds.length

  useEffect(() => {
    if (isPermissionsLoading || !hasUserData) {
      setState((prev) => ({ ...prev, isLoading: true }))
      return
    }

    // Navigation Rule 1: Universal site access → Allow settings access ONLY if site is selected
    if (hasUniversalAccess && currentSite) {
      setState({
        isLoading: false,
        shouldRedirect: false,
        redirectPath: null,
      })
      return
    }

    // Navigation Rule 2: No site access → Redirect to dashboard
    if (accessibleSiteCount === 0) {
      setState({
        isLoading: false,
        shouldRedirect: true,
        redirectPath: `/${ROUTES.dashboard.path}`,
      })
      return
    }

    // Navigation Rule 3: Single site access → Allow if current site matches
    if (accessibleSiteCount === 1 && currentSite) {
      const [singleSiteId] = accessibleSiteIds
      if (currentSite.siteId === singleSiteId) {
        setState({
          isLoading: false,
          shouldRedirect: false,
          redirectPath: null,
        })
        return
      }
    }

    // Navigation Rule 4 & 5: Multiple sites or no current site → Location selection
    if (!currentSite) {
      setState({
        isLoading: false,
        shouldRedirect: true,
        redirectPath: ROUTES.locationSelection.path,
      })
      return
    }

    // Verify current site is in accessible sites
    if (!accessibleSiteIds.includes(currentSite.siteId)) {
      setState({
        isLoading: false,
        shouldRedirect: true,
        redirectPath: ROUTES.locationSelection.path,
      })
      return
    }

    // All checks passed - allow access to settings
    setState({
      isLoading: false,
      shouldRedirect: false,
      redirectPath: null,
    })
  }, [
    isPermissionsLoading,
    hasUserData,
    currentSite?.siteId,
    accessibleSiteCount,
    hasUniversalAccess,
    hasExplicitBrandIntent,
  ])

  if (state.isLoading) {
    return <Loading />
  }

  if (state.shouldRedirect && state.redirectPath) {
    return <Navigate to={state.redirectPath} replace />
  }

  return <Outlet />
}

export default SettingsGuard
